// Display text names the live-streaming unit "ULLFU" (Ultra Low Latency Forwarding Unit). Visible
// text only: code names (ULLFU*, sfu*), API paths and console logs still say ULLFU.
import React, { useState, useEffect } from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { addCamera, editCamera, getNodeModels, getCameraModels, attachModelToCamera, detachModelFromCamera, setCameraLiveMode, getNodeCameras } from '../../services/api.service';
import {
  CameraTagFields, Modal, BTN_PRIMARY, BTN_SECONDARY, INPUT, LABEL, HINT, ptzSettingToApi, ptzSettingFromApi, refreshCameraTagCatalog, useCameraTagCatalog, type PtzSetting,
} from '@trinetra/shared';
import { cloudCameraTagsApi } from '../../api/cameraTags.adapter';
import GroupPickerPanel from '../groups/GroupPickerPanel';
import { useAuth } from '../../contexts/AuthContext';


interface CameraFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (camera?: any) => void;
  camera?: any | null;
  nodeId: string;
  groups?: any[];
  prefill?: Record<string, any> | null;
}

/**
 * Host portion of an RTSP URL. Mirrors the backend fallback (cameraService
 * .deriveIpFromRtspUrl) so the operator sees the value that would be stored
 * before saving, and can override it. Returns '' when there is nothing usable.
 */
function ipFromRtsp(url: string): string {
  if (!url) return '';
  let authority = String(url).trim().replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '');
  const at = authority.lastIndexOf('@');            // credentials may contain '@'
  if (at !== -1) authority = authority.slice(at + 1);
  authority = authority.split(/[/?#]/)[0];
  const v6 = authority.match(/^\[([^\]]+)\]/);
  const host = v6 ? v6[1] : authority.split(':')[0];
  return !host || /[<>\s]/.test(host) ? '' : host;
}

export default function CameraFormModal({ isOpen, onClose, onSuccess, camera, nodeId, groups = [], prefill }: CameraFormModalProps) {
  const isEdit = !!camera;

  const { hasPermission } = useAuth();
  const canConfig = hasPermission('cameras.config');
  const canAttach = hasPermission('ai-models.assign');

  const [form, setForm] = useState<any>({
    name: '', rtsp_url: '', sub_stream_rtsp_url: '', rtsp_username: '', rtsp_password: '',
    ip_address: '',
    manufacturer: '', model: '', location: '', group_ids: [] as string[],
    tag_ids: [] as string[], ptz: 'auto' as PtzSetting,
  });
  const [loading, setLoading] = useState(false);
  // Edit sends tag_ids only once the camera's real tags are known — saving before that would
  // wipe tags the form never showed.
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const [detectedPtz, setDetectedPtz] = useState<boolean | null>(null);
  const { catalog: tagCatalog } = useCameraTagCatalog(cloudCameraTagsApi);
  const [error, setError] = useState('');

  // AI Models (edit only)
  const [allModels, setAllModels] = useState<any[]>([]);
  const [cameraModels, setCameraModels] = useState<any[]>([]);

  // RTMP (edit only)
  const [rtmpLoading, setRtmpLoading] = useState(false);
  const [rtmpInfo, setRtmpInfo] = useState<any>(null);
  const [rtmpCopied, setRtmpCopied] = useState(false);
  const [rtmpAddressType, setRtmpAddressType] = useState('custom');

  // Reset form when modal opens or camera changes
  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setRtmpInfo(null);
    setAllModels([]);
    setCameraModels([]);
    if (isEdit) {
      setForm({
        name: camera.name || '',
        rtsp_url: camera.rtsp_url || '',
        sub_stream_rtsp_url: camera.sub_stream_rtsp_url || '',
        rtsp_username: camera.rtsp_username || '',
        rtsp_password: '',
        ip_address: camera.ip_address || '',
        manufacturer: camera.manufacturer || '',
        model: camera.model || '',
        location: camera.location || '',
        group_ids: camera.groups ? camera.groups.map((g: any) => g.id) : (camera.group_ids || []),
        tag_ids: Array.isArray(camera.tags) ? camera.tags.map((t: any) => t.id) : [],
        ptz: ptzSettingFromApi(camera.ptz_manual),
      });
      setDetectedPtz(camera.capabilities?.ptz ?? null);
      setTagsLoaded(Array.isArray(camera.tags));
      // Opened from somewhere whose camera object predates tags (or is partial): read this
      // camera's tags from the site's list — the cloud has no single-camera read.
      if (!Array.isArray(camera.tags) && nodeId) {
        const camId = camera.id || camera.camera_id;
        getNodeCameras(nodeId)
          .then((data: any) => {
            const list = Array.isArray(data) ? data : data?.data || [];
            const fresh = list.find((c: any) => (c.id || c.camera_id) === camId);
            if (!fresh) return;
            setForm((f: any) => ({ ...f, tag_ids: (fresh.tags || []).map((t: any) => t.id), ptz: ptzSettingFromApi(fresh.ptz_manual) }));
            setDetectedPtz(fresh.capabilities?.ptz ?? null);
            setTagsLoaded(Array.isArray(fresh.tags));
          })
          .catch(() => {});
      }
      // Load AI models
      if (nodeId) {
        const camId = camera.id || camera.camera_id;
        getNodeModels(nodeId, { limit: 100 })
          .then((res: any) => {
            console.log('[CameraFormModal] getNodeModels raw:', JSON.stringify(res));
            const arr = Array.isArray(res) ? res : (res?.data || []);
            console.log('[CameraFormModal] allModels parsed:', arr.length, arr.map((m: any) => ({ id: m.id, name: m.name })));
            setAllModels(Array.isArray(arr) ? arr : []);
          })
          .catch((err: any) => console.error('Failed to load models:', err));
        getCameraModels(nodeId, camId)
          .then((res: any) => {
            console.log('[CameraFormModal] getCameraModels raw:', JSON.stringify(res));
            const d = res?.data ?? res;
            console.log('[CameraFormModal] cameraModels parsed:', Array.isArray(d) ? d.length : 'not-array', d);
            setCameraModels(Array.isArray(d) ? d : []);
          })
          .catch((err: any) => console.error('Failed to load camera models:', err));
      }
    } else {
      const pf = prefill || {};
      setForm({
        name: pf.name || '',
        rtsp_url: pf.rtsp_url || '',
        sub_stream_rtsp_url: pf.sub_stream_rtsp_url || '',
        rtsp_username: pf.rtsp_username || '',
        rtsp_password: pf.rtsp_password || '',
        ip_address: pf.ip_address || ipFromRtsp(pf.rtsp_url),
        manufacturer: pf.manufacturer || '',
        model: pf.model || '',
        location: pf.location || '',
        group_ids: pf.group_ids || [],
        tag_ids: pf.tag_ids || [],
        ptz: 'auto',
      });
      setTagsLoaded(true);
      setDetectedPtz(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, camera?.id]);

  async function toggleModel(m: any) {
    if (!nodeId || !camera) return;
    const camId = camera.id || camera.camera_id;
    setError('');
    try {
      const isAttached = cameraModels.some((cm: any) => cm.id === m.id);
      if (isAttached) {
        await detachModelFromCamera(nodeId, m.id, camId);
      } else {
        await attachModelToCamera(nodeId, m.id, camId, {});
      }
      const res: any = await getCameraModels(nodeId, camId);
      const d = res?.data ?? res;
      setCameraModels(Array.isArray(d) ? d : []);
    } catch (err: any) {
      // The node's answer used to go only to the console, so a refused assignment
      // looked like a checkbox that simply would not tick. Say what came back.
      console.error('Model toggle failed:', err);
      setError(err?.response?.data?.message || err?.message || `Could not update ${m.name || 'model'} on this camera`);
    }
  }

  /** Typing the stream URL fills the IP for as long as the field still holds the
   *  derived value — once the operator types their own IP we stop touching it. */
  function handleRtspChange(next: string) {
    setForm((f: any) => ({
      ...f,
      rtsp_url: next,
      ip_address: (!f.ip_address || f.ip_address === ipFromRtsp(f.rtsp_url)) ? ipFromRtsp(next) : f.ip_address,
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nodeId) return;
    setLoading(true);
    setError('');
    try {
      const { ptz, ...rest } = form;
      const payload: any = { ...rest, ptz_manual: ptzSettingToApi(ptz) };
      if (!tagsLoaded) delete payload.tag_ids;
      if (isEdit) {
        if (!payload.rtsp_password) delete payload.rtsp_password;
        if (!payload.rtsp_username) payload.rtsp_username = null;
        if (!payload.manufacturer) payload.manufacturer = null;
        if (!payload.model) payload.model = null;
        if (!payload.location) payload.location = null;
        const camId = camera.id || camera.camera_id;
        await editCamera(nodeId, camId, payload);
      } else {
        await addCamera(nodeId, payload);
      }
      refreshCameraTagCatalog(cloudCameraTagsApi); // tag counts in the filter column
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'add'} camera`);
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  const inputClass = INPUT;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Camera' : 'Add New Camera'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={BTN_SECONDARY}>
            Cancel
          </button>
          <button type="submit" form="camera-form" disabled={loading} className={BTN_PRIMARY}>
            {loading ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save Changes' : 'Add Camera')}
          </button>
        </div>
      }
    >
        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Form */}
        <form id="camera-form" onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Name */}
          <div>
            <label className={LABEL}>Name *</label>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Front Door Camera" className={inputClass} />
          </div>

          {/* RTSP URL */}
          <div>
            <label className={LABEL}>RTSP URL *</label>
            <input required value={form.rtsp_url} onChange={e => handleRtspChange(e.target.value)}
              placeholder="rtsp://192.168.1.x:554/stream1" className={`${inputClass} font-mono`} />
          </div>

          {/* Sub Stream */}
          <div>
            <label className={LABEL}>Sub Stream RTSP URL</label>
            <input value={form.sub_stream_rtsp_url} onChange={e => setForm({ ...form, sub_stream_rtsp_url: e.target.value })}
              placeholder="rtsp://192.168.1.x:554/stream2 (optional)" className={`${inputClass} font-mono`} />
          </div>

          {/* Username / Password */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>RTSP Username</label>
              <input value={form.rtsp_username} onChange={e => setForm({ ...form, rtsp_username: e.target.value })}
                placeholder="admin" className={inputClass} />
            </div>
            <div>
              <label className={LABEL}>RTSP Password</label>
              <input type="password" value={form.rtsp_password} onChange={e => setForm({ ...form, rtsp_password: e.target.value })}
                placeholder={isEdit ? 'Leave empty to keep current' : 'optional'}
                className={inputClass} />
            </div>
          </div>

          {/* Manufacturer / Model */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Manufacturer</label>
              <input value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })}
                placeholder="e.g., Hikvision" className={inputClass} />
            </div>
            <div>
              <label className={LABEL}>Model</label>
              <input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}
                placeholder="e.g., DS-2CD2143" className={inputClass} />
            </div>
          </div>

          {/* IP Address / Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>IP Address</label>
              <input value={form.ip_address} onChange={e => setForm({ ...form, ip_address: e.target.value })}
                placeholder="e.g., 192.168.1.50" className={`${inputClass} font-mono`} />
              <p className={HINT}>Leave blank to take it from the RTSP URL.</p>
            </div>
            <div>
              <label className={LABEL}>Location</label>
              <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                placeholder="e.g., Front Gate, Building A" className={inputClass} />
            </div>
          </div>

          {/* ── AI Analytics Models (edit only) ── */}
          {isEdit && canAttach && allModels.length > 0 && (
            <div>
              <label className={LABEL}>AI Analytics Models</label>
              <p className="text-[11px] text-muted-foreground mb-2">Select models to run on this camera</p>
              <div className="flex flex-wrap gap-2">
                {allModels.map((m: any) => {
                  const isAttached = cameraModels.some((cm: any) => cm.id === m.id);
                  return (
                    <button key={m.id} type="button" onClick={() => toggleModel(m)}
                      className={`inline-flex items-center rounded-full px-3 py-1 text-[12px] font-medium transition-colors border ${isAttached
                        ? 'bg-brand/15 text-brand border-brand/30 hover:bg-brand/25'
                        : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
                        }`}>
                      {isAttached ? '✓ ' : '+ '}{m.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── RTMP Live Source (edit only) ── */}
          {isEdit && canConfig && (() => {
            const camId = camera.id || camera.camera_id;
            const streamKey = camera.rtmp_stream_key || rtmpInfo?.stream_key || '';
            const rtmpPort = rtmpInfo?.rtmp_port || 1935;
            const trinetraHost = camera.node_ip || window.location.hostname;
            const customAddress = `/live/${camId}?key=${streamKey}`;
            const fullUrl = `rtmp://${trinetraHost}:${rtmpPort}${customAddress}`;
            const isCustom = rtmpAddressType === 'custom';
            return (
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-[13px] font-semibold text-foreground">RTMP Live Source</h4>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {camera.live_mode === 'rtmp'
                        ? 'Camera pushes H264 video via RTMP to the ULLFU'
                        : 'Ingest service pulls video from camera via RTSP (default)'}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={rtmpLoading}
                    onClick={async () => {
                      const newMode = camera.live_mode === 'rtmp' ? 'rtsp' : 'rtmp';
                      setRtmpLoading(true);
                      try {
                        const result = await setCameraLiveMode(nodeId, camId, newMode);
                        if (newMode === 'rtmp' && result) setRtmpInfo(result);
                        else setRtmpInfo(null);
                        onSuccess?.();
                      } catch (err: any) {
                        setError('Failed to switch live mode: ' + err.message);
                      } finally {
                        setRtmpLoading(false);
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${camera.live_mode === 'rtmp' ? 'bg-brand' : 'bg-muted-foreground/30'
                      } ${rtmpLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${camera.live_mode === 'rtmp' ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${camera.live_mode === 'rtmp'
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                    {camera.live_mode === 'rtmp' ? 'RTMP Push' : 'RTSP Pull'}
                  </span>
                  {rtmpLoading && <span className="text-[11px] text-muted-foreground animate-pulse">Switching…</span>}
                </div>

                {/* RTMP config shown when enabled */}
                {(camera.live_mode === 'rtmp' || rtmpInfo) && (
                  <div className="space-y-3 pt-1">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Camera RTMP Configuration</p>
                    <p className="text-[11px] text-muted-foreground">Enter these values in your camera's RTMP settings:</p>

                    {/* Address Type Selector */}
                    <div>
                      <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Address Type</label>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => setRtmpAddressType('custom')}
                          className={`px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${isCustom ? 'bg-brand text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                          Custom
                        </button>
                        <button type="button" onClick={() => setRtmpAddressType('ip')}
                          className={`px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${!isCustom ? 'bg-brand text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                          IP / Domain
                        </button>
                      </div>
                    </div>

                    <div className={`grid grid-cols-2 gap-3 ${isCustom ? 'opacity-40 pointer-events-none' : ''}`}>
                      <CopyField label="IP Address" value={trinetraHost} onCopy={() => { setRtmpCopied(true); setTimeout(() => setRtmpCopied(false), 1500); }} disabled={isCustom} />
                      <CopyField label="Port" value={String(rtmpPort)} onCopy={() => { setRtmpCopied(true); setTimeout(() => setRtmpCopied(false), 1500); }} disabled={isCustom} />
                    </div>

                    <div className={isCustom ? 'opacity-40 pointer-events-none' : ''}>
                      <CopyField label="Stream Path" value={customAddress} onCopy={() => { setRtmpCopied(true); setTimeout(() => setRtmpCopied(false), 1500); }} disabled={isCustom} />
                    </div>

                    <div className={!isCustom ? 'opacity-40 pointer-events-none' : ''}>
                      <CopyField label="Custom Address" value={fullUrl} onCopy={() => { setRtmpCopied(true); setTimeout(() => setRtmpCopied(false), 1500); }} disabled={!isCustom} />
                    </div>

                    {rtmpCopied && <p className="text-[11px] text-green-600 font-medium">✓ Copied to clipboard</p>}

                    <div className="rounded border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/20 px-3 py-2">
                      <p className="text-[11px] text-amber-700 dark:text-amber-400">
                        {isCustom
                          ? <><strong>Custom mode:</strong> Set Address Type to <strong>Custom</strong> in camera. Paste the <strong>Custom Address</strong> above. Set codec to <strong>H.264</strong>, disable audio.</>
                          : <><strong>IP/Domain mode:</strong> Enter the <strong>IP Address</strong> and <strong>Port</strong> above. Set Stream Path to <code className="bg-amber-100 dark:bg-amber-800/50 px-1 rounded">{customAddress}</code>. Set codec to <strong>H.264</strong>, disable audio.</>
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Tags + PTZ — what the camera list's filter column filters on. */}
          <CameraTagFields
            catalog={tagCatalog}
            tagIds={form.tag_ids || []}
            onTagsChange={(ids) => setForm((f: any) => ({ ...f, tag_ids: ids }))}
            ptz={form.ptz}
            onPtzChange={(v) => setForm((f: any) => ({ ...f, ptz: v }))}
            detectedPtz={isEdit ? detectedPtz : undefined}
          />

          {/* Groups */}
          <GroupPickerPanel
            groups={groups}
            selectedIds={form.group_ids || []}
            onChange={(ids: string[]) => setForm({ ...form, group_ids: ids })}
            label="Assign to Groups"
            multiple
          />

        </form>
    </Modal>
  );
}

function CopyField({ label, value, onCopy, disabled }: { label: string; value: string; onCopy: () => void; disabled?: boolean }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-muted-foreground">{label}</label>
      <div className="flex items-center gap-1">
        <code className="flex-1 rounded border border-border bg-card px-2.5 py-1.5 text-[12px] font-mono text-foreground break-all select-all">{value}</code>
        <button type="button" disabled={disabled}
          onClick={() => { navigator.clipboard.writeText(value); onCopy?.(); }}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" title="Copy" aria-label={`Copy ${label}`}>
          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
